import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { InboxOutlined } from '@mui/icons-material';
import type { ReactNode } from 'react';
import { humanizeError } from '@/utils/errorMessage';
import { ApiError } from '@/lib/apiClient';

interface Props {
  loading?: boolean;
  error?: unknown;
  isEmpty?: boolean;
  emptyText?: string;
  emptyIcon?: ReactNode;
  /** Optional call-to-action shown inside the empty state (e.g. a "Create" button). */
  emptyAction?: ReactNode;
  /** Optional bold heading above {@link emptyText}, e.g. "No branches yet". */
  emptyTitle?: string;
  /**
   * For list/collection views: treat a 404 / NOT_FOUND response as "no records"
   * and render the friendly empty state instead of an error banner. Do NOT enable
   * on detail views, where a 404 genuinely means the record is missing.
   */
  notFoundAsEmpty?: boolean;
  onRetry?: () => void;
  /** Optional text under the spinner, e.g. "Loading bookings…". */
  loadingText?: string;
  /** Smaller paddings — for states shown inside a card section, a dialog or a side panel. */
  compact?: boolean;
  children: ReactNode;
}

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 404 || error.errorCode === 'NOT_FOUND');
}

/** Wraps query-driven content with consistent loading / error / empty states. */
export default function DataState({
  loading,
  error,
  isEmpty,
  emptyText = 'Nothing here yet.',
  emptyIcon,
  emptyAction,
  emptyTitle,
  notFoundAsEmpty,
  onRetry,
  loadingText,
  compact,
  children,
}: Props) {
  const py = compact ? 3 : { xs: 4, sm: 6 };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1.25} sx={{ py }} role="status" aria-live="polite">
        <CircularProgress size={compact ? 24 : 32} />
        {loadingText && (
          <Typography variant="caption" color="text.secondary">
            {loadingText}
          </Typography>
        )}
      </Stack>
    );
  }

  // A 404 on a collection endpoint means "no records", not a failure — show the
  // friendly empty state rather than an alarming error banner.
  // A failed load has no rows either; that is not "empty", and saying so hides the failure.
  const treatAsEmpty = (isEmpty && !error) || (notFoundAsEmpty && isNotFound(error));

  if (error && !treatAsEmpty) {
    const msg = humanizeError(error, 'We couldn’t load this. Please try again.');
    return (
      <Box sx={{ p: compact ? 1 : { xs: 1.5, sm: 2 } }}>
        <Alert
          severity="error"
          variant="outlined"
          action={
            onRetry ? (
              <Button color="inherit" size="small" onClick={onRetry}>
                Retry
              </Button>
            ) : undefined
          }
          sx={{ alignItems: 'center', '& .MuiAlert-message': { minWidth: 0, overflowWrap: 'anywhere' } }}
        >
          {msg}
        </Alert>
      </Box>
    );
  }

  if (treatAsEmpty) {
    return (
      <Stack
        alignItems="center"
        spacing={1}
        sx={{ py, px: 2, color: 'text.secondary', textAlign: 'center' }}
      >
        <Box sx={{ display: 'flex', color: 'text.disabled', '& .MuiSvgIcon-root': { fontSize: compact ? 36 : 44 } }}>
          {emptyIcon ?? <InboxOutlined />}
        </Box>
        {emptyTitle && (
          <Typography variant="subtitle1" color="text.primary">
            {emptyTitle}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          {emptyText}
        </Typography>
        {emptyAction && <Box sx={{ pt: 0.5 }}>{emptyAction}</Box>}
      </Stack>
    );
  }

  return <>{children}</>;
}
